import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import {
  FiInstagram,
  FiTwitter,
  FiFacebook,
  FiYoutube,
  FiLinkedin,
  FiSend,
  FiMail,
  FiPhone,
  FiMapPin,
  FiGlobe,
} from 'react-icons/fi';
import { FaWhatsapp, FaImdb, FaVimeo, FaTumblr } from 'react-icons/fa';
import ThreeBackground from './ThreeBackground';

const socials = [
  { icon: FiGlobe,     label: 'Website',   color: '#4285F4', href: 'https://magicmoonstudios.com/' },
  { icon: FiInstagram, label: 'Instagram', color: '#E1306C', href: 'https://www.instagram.com/magicmoonstudios/' },
  { icon: FiLinkedin,  label: 'LinkedIn',  color: '#0A66C2', href: 'https://in.linkedin.com/in/magicmoon-studios-41a74a323' },
  { icon: FaTumblr,    label: 'Tumblr',    color: '#36465D', href: 'https://www.tumblr.com/magicmoonstudios' },
  { icon: FiYoutube,   label: 'YouTube',   color: '#FF0000', href: 'https://www.youtube.com/@magicmoonstudios' },
  { icon: FiTwitter,   label: 'Twitter / X', color: '#1DA1F2', href: '#' },
  { icon: FiFacebook,  label: 'Facebook',  color: '#1877F2', href: 'https://www.facebook.com/MagicMoonProduction/' },
  { icon: FaWhatsapp,  label: 'WhatsApp',  color: '#25D366', href: '#' },
  { icon: FaImdb,      label: 'IMDb',      color: '#F5C518', href: '#' },
  { icon: FaVimeo,     label: 'Vimeo',     color: '#1AB7EA', href: '#' },
];

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await addDoc(collection(db, 'contacts'), {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        createdAt: serverTimestamp(),
      });
      setSent(true);
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      console.error('Firestore error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 bg-brand-black overflow-hidden">
      {/* Three.js orbital rings background */}
      <ThreeBackground type="rings" />

      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold/30 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="section-label block mb-4">Get In Touch</span>
          <h2 className="font-cinzel font-black text-4xl md:text-5xl text-white mb-5">
            Contact <span className="text-gold-gradient">Magic Moon</span>
          </h2>
          <div className="gold-divider mb-6" />
          <p className="font-inter text-white/50 max-w-xl mx-auto text-sm leading-relaxed">
            Ready to create something legendary? Reach out to our team and let
            us bring your vision to life on the grandest stage.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left — info + socials */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="flex flex-col gap-8"
          >
            {/* Contact info cards */}
            {[
              { icon: FiMail,   label: 'Email Us',      value: 'hello@magicmoonstudios.com' },
              { icon: FiPhone,  label: 'Call Us',       value: '082964 11896' },
              { icon: FiMapPin, label: 'Visit Us',      value: 'No 141/1, 1st K Block, 1st Main, Dr Rajkumar Rd, 2nd Stage, Rajajinagar, Bengaluru 560010' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                  className="glass-card flex items-start gap-5 p-5 rounded-sm"
                >
                  <div className="w-11 h-11 rounded-sm bg-gradient-to-br from-brand-orange/20 to-brand-gold/10 border border-brand-orange/30 flex items-center justify-center flex-shrink-0">
                    <Icon size={18} color="#f37021" />
                  </div>
                  <div>
                    <p className="font-cinzel text-[10px] tracking-widest text-brand-gold/60 uppercase mb-1">
                      {item.label}
                    </p>
                    <p className="font-inter text-white/70 text-sm">{item.value}</p>
                  </div>
                </motion.div>
              );
            })}

            {/* Social Media */}
            <div>
              <p className="font-cinzel text-[10px] tracking-widest text-brand-gold/60 uppercase mb-5">
                Follow Us
              </p>
              <div className="grid grid-cols-4 gap-3">
                {socials.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <motion.a
                      key={i}
                      href={s.href}
                      aria-label={s.label}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.1, y: -3 }}
                      whileTap={{ scale: 0.95 }}
                      className="glass-card flex flex-col items-center gap-2 py-4 rounded-sm group"
                    >
                      <Icon
                        size={20}
                        style={{ color: s.color, transition: 'all 0.3s' }}
                      />
                      <span className="text-[9px] font-inter text-white/30 group-hover:text-white/60 transition-colors tracking-wide text-center leading-none">
                        {s.label}
                      </span>
                    </motion.a>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Right — contact form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="glass-card p-8 md:p-10 rounded-sm"
          >
            {sent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="h-full flex flex-col items-center justify-center text-center gap-6 py-12"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-brand-orange to-brand-gold flex items-center justify-center">
                  <FiSend size={32} color="#0a0a0a" />
                </div>
                <h3 className="font-cinzel font-bold text-white text-2xl">
                  Message Sent
                </h3>
                <p className="font-inter text-white/50 text-sm leading-relaxed max-w-sm">
                  Thank you for reaching out to Magic Moon Studios. Our team will
                  get back to you within 24 hours.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="btn-royal px-8 py-3 text-xs rounded-sm"
                >
                  Send Another
                </button>
              </motion.div>
            ) : (
              <>
                <h3 className="font-cinzel font-bold text-white text-xl mb-8">
                  Send a Message
                </h3>
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Name */}
                    <div className="flex flex-col gap-2">
                      <label className="font-cinzel text-[10px] tracking-widest text-brand-gold/60 uppercase">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your Name"
                        className="bg-white/5 border border-white/10 focus:border-brand-orange outline-none text-white placeholder-white/20 font-inter text-sm px-4 py-3 rounded-sm transition-colors duration-300"
                      />
                    </div>
                    {/* Email */}
                    <div className="flex flex-col gap-2">
                      <label className="font-cinzel text-[10px] tracking-widest text-brand-gold/60 uppercase">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="bg-white/5 border border-white/10 focus:border-brand-orange outline-none text-white placeholder-white/20 font-inter text-sm px-4 py-3 rounded-sm transition-colors duration-300"
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div className="flex flex-col gap-2">
                    <label className="font-cinzel text-[10px] tracking-widest text-brand-gold/60 uppercase">
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="Project Inquiry, Collaboration..."
                      className="bg-white/5 border border-white/10 focus:border-brand-orange outline-none text-white placeholder-white/20 font-inter text-sm px-4 py-3 rounded-sm transition-colors duration-300"
                    />
                  </div>

                  {/* Message */}
                  <div className="flex flex-col gap-2">
                    <label className="font-cinzel text-[10px] tracking-widest text-brand-gold/60 uppercase">
                      Message
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project..."
                      className="bg-white/5 border border-white/10 focus:border-brand-orange outline-none text-white placeholder-white/20 font-inter text-sm px-4 py-3 rounded-sm resize-none transition-colors duration-300"
                    />
                  </div>

                  {error && (
                    <p className="font-inter text-red-400 text-xs text-center">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-royal px-8 py-4 text-sm rounded-sm flex items-center justify-center gap-3 mt-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-brand-black/40 border-t-brand-black rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FiSend size={16} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </div>

        {/* Google Map */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-16 w-full h-[450px] rounded-sm overflow-hidden glass-card border border-brand-orange/20"
        >
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.5268877354024!2d77.5531531!3d13.002083999999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae3d005535b953%3A0x2ece0cb4c76d4196!2sMagic%20Moon%20Studios%20Bengaluru!5e0!3m2!1sen!2sin!4v1789290306379!5m2!1sen!2sin" 
            width="100%" 
            height="100%" 
            style={{ border: 0 }} 
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
            title="Magic Moon Studios Bengaluru Location"
          ></iframe>
        </motion.div>
      </div>
    </section>
  );
}
